/**
 * 权限位编辑器。
 *
 * 老系统的权限设置页（rightedit.jsp）是一排没有说明的字母复选框，
 * 管理员得对着文档才知道 `U` 和 `S` 分别是什么。这里按用途分组并写清中文。
 */

import { formatPerm, Perm } from '../../lib/protocol'

interface Props {
  value: number
  onChange: (v: number) => void
  /** 只读展示 */
  readOnly?: boolean
}

/** 权限按用途分组，每组一行。 */
const GROUPS: Array<{ title: string; items: Array<{ bit: number; label: string; hint?: string }> }> = [
  {
    title: '基础交流',
    items: [
      { bit: Perm.Read, label: '接收文本' },
      { bit: Perm.Write, label: '发送文本' },
      { bit: Perm.Listen, label: '接收语音' },
      { bit: Perm.Speak, label: '申请发言' },
      { bit: Perm.PrivateMsg, label: '私聊' },
      { bit: Perm.Video, label: '发送视频' },
    ],
  },
  {
    title: '教学工具',
    items: [
      { bit: Perm.Whiteboard, label: '使用白板' },
      { bit: Perm.OpWB, label: '操作他人板书' },
      { bit: Perm.DelWB, label: '删除板书' },
      { bit: Perm.LockWB, label: '锁定白板' },
      { bit: Perm.AppShare, label: '屏幕共享' },
      { bit: Perm.SendFile, label: '传送文件' },
      { bit: Perm.Record, label: '录制课堂' },
      { bit: Perm.HTML, label: 'HTML 共享' },
    ],
  },
  {
    title: '课堂管理',
    items: [
      { bit: Perm.Op, label: '主持人', hint: '拥有课堂控制权' },
      { bit: Perm.SetMic, label: '给麦/放麦/锁麦' },
      { bit: Perm.SetMicNum, label: '设定麦位数' },
      { bit: Perm.KickUser, label: '请人出教室' },
      { bit: Perm.SetRoomProp, label: '修改房间设置' },
      { bit: Perm.Broadcast, label: '发布公告' },
      { bit: Perm.LockRoom, label: '锁定房间' },
    ],
  },
  {
    title: '高级权限',
    items: [
      { bit: Perm.SetUserProp, label: '修改他人权限', hint: '慎给：可改动其他人的能力' },
      { bit: Perm.SetOp, label: '任免主持人' },
      { bit: Perm.SetAdmin, label: '设置临时管理员' },
      { bit: Perm.BanIP, label: '封禁 IP' },
      { bit: Perm.Hide, label: '隐身进入' },
      { bit: Perm.SeeHide, label: '可见隐身用户' },
      { bit: Perm.Admin, label: '后台管理员', hint: '可登录管理后台' },
      { bit: Perm.Super, label: '超级权限', hint: '不受任何限制，仅限系统管理员' },
    ],
  },
]

export function PermEditor({ value, onChange, readOnly }: Props) {
  const toggle = (bit: number) => {
    if (readOnly) return
    onChange((value & bit) !== 0 ? value & ~bit : value | bit)
  }

  return (
    <div className="perm-editor">
      {GROUPS.map((g) => (
        <div key={g.title} className="perm-group">
          <div className="perm-group-title">{g.title}</div>
          <div className="perm-items">
            {g.items.map((it) => (
              <label
                key={it.bit}
                className={`perm-item${(value & it.bit) !== 0 ? ' on' : ''}${readOnly ? ' ro' : ''}`}
                title={it.hint}
              >
                <input
                  type="checkbox"
                  checked={(value & it.bit) !== 0}
                  disabled={readOnly}
                  onChange={() => toggle(it.bit)}
                />
                <span>{it.label}</span>
              </label>
            ))}
          </div>
        </div>
      ))}
      <div className="perm-raw">
        存库格式：<code>{formatPerm(value) || '（空）'}</code>
        <span className="dim">（与老系统一致，可直接对照老库 room.text / gradeinfo.text）</span>
      </div>
    </div>
  )
}
